import React, { useEffect, useState } from "react";
import api from "../lib/api";
import ContentDetail from "../components/ContentDetail";
import GenerateDialog from "../components/GenerateDialog";
import { StatusBadge, DataLabel } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkle } from "@phosphor-icons/react";

export default function ContentLibrary() {
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState({ content_types: [], universes: [] });
  const [filters, setFilters] = useState({ status: "all", content_type: "all", universe: "all", evidence: "all" });
  const [sort, setSort] = useState("recent");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "all") p.append(k, v); });
    if (sort !== "recent") p.append("sort", sort);
    const { data } = await api.get(`/content?${p.toString()}`);
    setItems(data);
  };
  useEffect(() => { api.get("/config/taxonomy").then((r) => setTax(r.data)); }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [JSON.stringify(filters), sort]);

  const typeLabel = (k) => tax.content_types.find((t) => t.key === k)?.label || k;
  const uniLabel = (k) => tax.universes.find((u) => u.key === k)?.label || k;

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Bibliothèque</div>
          <h1 className="font-serif text-4xl text-brand tracking-tight">Contenus</h1>
        </div>
        <GenerateDialog onStarted={load} trigger={
          <button className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
            <Sparkle size={16} weight="fill" /> Générer</button>} />
      </div>

      <div className="flex gap-3 mb-4">
        {[
          { key: "status", options: [["all", "Tous statuts"], ["published", "Publié"], ["pending_validation", "À valider"], ["needs_update", "À actualiser"], ["draft", "Brouillon"], ["rejected", "Rejeté"]] },
          { key: "content_type", options: [["all", "Tous types"], ...tax.content_types.map((t) => [t.key, t.label])] },
          { key: "universe", options: [["all", "Tous univers"], ...tax.universes.map((u) => [u.key, u.label])] },
          { key: "evidence", options: [["all", "Tous niveaux de preuve"], ["5", "Méta-analyse / revue"], ["4", "Essai randomisé"], ["3", "Cohorte"], ["2", "Cas-témoins"], ["1", "Série de cas"], ["0", "Synthèse littérature"]] },
        ].map((f) => (
          <Select key={f.key} value={filters[f.key]} onValueChange={(v) => setFilters({ ...filters, [f.key]: v })}>
            <SelectTrigger data-testid={`filter-${f.key}`} className="w-48 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Trier</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="w-56 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Plus récent</SelectItem>
              <SelectItem value="evidence">Niveau de preuve (fort → faible)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Titre</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Univers</th>
              <th className="px-4 py-3">Preuve</th>
              <th className="px-4 py-3 text-center">SEO</th><th className="px-4 py-3 text-center">GEO</th><th className="px-4 py-3">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Aucun contenu.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} data-testid={`content-row-${c.id}`} onClick={() => setOpen(c.id)}
                className="cursor-pointer hover:bg-brand-soft/40 transition-colors">
                <td className="px-4 py-3 font-medium max-w-md truncate">{c.title_primary}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{typeLabel(c.content_type)}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{uniLabel(c.universe)}</td>
                <td className="px-4 py-3 text-xs" data-testid={`evidence-cell-${c.id}`}>
                  {c.evidence_level?.label ? (
                    <span className="inline-flex items-center gap-1 bg-brand-soft text-brand rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
                      {c.evidence_level.label}
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-3 text-center font-mono text-xs text-brand">{c.seo_score}</td>
                <td className="px-4 py-3 text-center font-mono text-xs text-ai">{c.geo_score}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="sr-only"><DialogTitle>Détail du contenu</DialogTitle>
            <DialogDescription>Relecture, métadonnées SEO/GEO et validation du contenu.</DialogDescription></DialogHeader>
          {open && <ContentDetail contentId={open} onChanged={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
